import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import CategoryManagement from '../components/admin/CategoryManagement';
import ServiceProviderManagement from '../components/admin/ServiceProviderManagement';
import AdvertisingManagement from '../components/admin/AdvertisingManagement';
import NotificationsPanel from '../components/admin/NotificationsPanel';
import Analytics from '../components/admin/Analytics';
import ServiceProvidersList from '../components/admin/ServiceProvidersList';
import { collection, query, where, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import { validateAndUpdatePhoneNumbers } from '../utils/phoneUtils';
import TestingPanel from '../components/admin/TestingPanel';

const AdminDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'categories');
  const [pendingCount, setPendingCount] = useState(0);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const tabs = [
    { id: 'categories', label: 'Category Management', icon: 'fa-tags' },
    { id: 'providers', label: 'Service Providers', icon: 'fa-list' },
    { id: 'management', label: 'Provider Management', icon: 'fa-user-plus' },
    { id: 'advertising', label: 'Advertising', icon: 'fa-ad' },
    { id: 'notifications', label: 'Notifications', icon: 'fa-bell' },
    { id: 'analytics', label: 'Analytics', icon: 'fa-chart-line' },
    { id: 'testing', label: 'Testing Panel', component: TestingPanel }
  ];

  useEffect(() => {
    const fetchPendingCount = async () => {
      const q = query(
        collection(db, 'businessRequests'),
        where('status', '==', 'pending')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setPendingCount(snapshot.docs.length);
      });

      return () => unsubscribe();
    };

    fetchPendingCount();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDelete = async (providerId) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setMessage('You must be logged in to delete a provider');
        return;
      }

      if (user.email !== 'admin@aroundu.co.za') {
        setMessage('Insufficient permissions: Admin access required');
        return;
      }

      await deleteDoc(doc(db, 'providers', providerId));
      setMessage('Provider deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(error.message || 'Error deleting provider');
    }
  };

  const handleValidatePhoneNumbers = async () => {
    try {
      setIsLoading(true);
      const results = await validateAndUpdatePhoneNumbers();
      console.log('Phone number validation results:', results);
      
      const invalidNumbersList = results.invalid.map(item => `
        <tr>
          <td>${item.providerName}</td>
          <td>${item.originalNumber}</td>
          <td>
            <button onclick="updatePhoneNumber('${item.providerId}')" class="fix-button">
              Fix Number
            </button>
          </td>
        </tr>
      `).join('');

      const resultsDiv = document.createElement('div');
      resultsDiv.className = 'validation-results';
      resultsDiv.innerHTML = `
        <div class="results-summary">
          <h3>Phone Number Validation Results</h3>
          <div class="stats">
            <div class="stat-item">
              <span class="stat-label">Total Providers:</span>
              <span class="stat-value">${results.total}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Valid Numbers:</span>
              <span class="stat-value success">${results.valid}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Invalid Numbers:</span>
              <span class="stat-value error">${results.invalid.length}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Updated Numbers:</span>
              <span class="stat-value info">${results.updated}</span>
            </div>
          </div>
          ${results.invalid.length > 0 ? `
            <div class="invalid-numbers">
              <h4>Invalid Numbers Found:</h4>
              <table class="invalid-numbers-table">
                <thead>
                  <tr>
                    <th>Provider Name</th>
                    <th>Invalid Number</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  ${invalidNumbersList}
                </tbody>
              </table>
            </div>
          ` : ''}
        </div>
      `;

      const existingResults = document.querySelector('.validation-results');
      if (existingResults) {
        existingResults.remove();
      }

      document.querySelector('.admin-dashboard').appendChild(resultsDiv);

    } catch (error) {
      console.error('Error validating phone numbers:', error);
      alert('Error validating phone numbers. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'categories':
        return <CategoryManagement />;
      case 'providers':
        return <ServiceProvidersList 
          isAdmin={true} 
          onDelete={handleDelete}
        />;
      case 'management':
        return <ServiceProviderManagement />;
      case 'advertising':
        return <AdvertisingManagement />;
      case 'notifications':
        return <NotificationsPanel />;
      case 'analytics':
        return <Analytics />;
      case 'testing':
        return <TestingPanel />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="admin-dashboard">
        <h1>Admin Dashboard</h1>
        
        <div className="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`}></i>
              <span>{tab.label}</span>
              {tab.id === 'notifications' && pendingCount > 0 && (
                <span className="notification-badge">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {renderTabContent()}
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;